/**
 * One declaration of a live action, from which the manifest entry, the payload
 * type and the handler signature are all derived.
 *
 * Before this existed, a game with five actions wrote each of them three times:
 * once in `manifest.json`, once as a `validate:` argument, and once as a
 * destructuring pattern in a handler. Nothing connected the three, and every
 * failure was silent — a handler registered under a key the manifest does not
 * declare can never fire, and a declared key with no handler answers
 * `UNKNOWN_ACTION` on somebody's live stream.
 *
 * ```ts
 * export const ACTIONS = defineActions({
 *   spawn_wave: {
 *     label: '敵の波',
 *     description: '…',
 *     safetyClass: 'GAMEPLAY',
 *     payloadSchema: {
 *       type: 'object',
 *       additionalProperties: false,
 *       required: ['count'],
 *       properties: { count: { type: 'integer', minimum: 1, maximum: 5 } },
 *     },
 *     defaultLimits: { ttlMs: 5000, maxPerMinute: 60, queueDepth: 20, overflowPolicy: 'DROP_NEWEST' },
 *     previewPayload: { count: 3 },
 *   },
 * });
 *
 * ACTIONS.register(game, {
 *   spawn_wave: ({ count }) => spawnWave(count),   // count: number, checked
 * });
 * ```
 *
 * The signature takes a `const` type parameter, so payload types are inferred
 * from an ordinary object literal — no `as const` needed, and adding one changes
 * nothing.
 *
 * A game that keeps `manifest.json` as its source of truth passes the array in
 * instead:
 *
 * ```js
 * import manifest from '../manifest.json';
 * export const ACTIONS = defineActions(manifest.liveActions);
 * ```
 *
 * That form is accepted at runtime with every check applied, but a JSON import
 * has already widened `safetyClass` to `string` and dropped every literal, so no
 * payload type can be recovered from it: handlers receive
 * `Record<string, unknown>`. Declaring in TypeScript and writing
 * `manifestFragment()` into the manifest is the typed direction.
 *
 * ## What is deliberately *not* inferred
 *
 * `PayloadOf` mirrors `payload-schema.ts` exactly, and falls back to `unknown`
 * wherever it cannot be certain. That includes a schema carrying a keyword the
 * SDK's compiler would refuse, and a `required` array that has widened to
 * `string[]` — in the latter case every property becomes optional, because the
 * alternative (trusting a widened `required`) types a possibly-absent field as
 * present, and a handler that adds `undefined` to a number gets `NaN` on a live
 * stream. A type that is too wide is an inconvenience; a type that is wrong in
 * the confident direction is the bug this module exists to prevent.
 */
import { TRYPORTALError } from './errors.js';
import { compilePayloadSchema } from './payload-schema.js';
import { STANDARD_LIVE_ACTION_KEYS } from './stream-mode.js';
const KEY_PATTERN = /^[a-z][a-z0-9_]{1,63}$/;
const RESERVED_PREFIX = 'tp_live_';
const CANONICAL_RESERVED_KEYS = Object.values(STANDARD_LIVE_ACTION_KEYS);
const SAFETY_CLASSES = new Set(['COSMETIC', 'GAMEPLAY', 'INTERRUPTIVE']);
const OVERFLOW_POLICIES = new Set([
    'DROP_NEWEST',
    'DROP_OLDEST',
    'COALESCE',
    'REPLACE_LATEST',
    'PAUSE_RULE',
]);
const BODY_FIELDS = new Set([
    'label',
    'description',
    'safetyClass',
    'payloadSchema',
    'defaultLimits',
    'previewPayload',
]);
const LIMIT_FIELDS = new Set([
    'ttlMs',
    'maxPerMinute',
    'queueDepth',
    'overflowPolicy',
    'perActorCooldownMs',
    'maxPayloadBytes',
]);
function invalid(message) {
    throw new TRYPORTALError('INVALID_USAGE', message);
}
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
/**
 * A JSON string whose object keys are sorted, at every depth.
 *
 * `assertMatchesManifest` compares serialised entries, and `JSON.stringify`
 * preserves insertion order — so a `jq -S` pass over `manifest.json`, or a hand
 * edit that moved `type` above `required`, would otherwise report a drift that
 * does not exist and send the reader to run a sync step that cannot fix it.
 *
 * Exported for `define-bindings.ts`, which compares manifest entries the same
 * way and must answer the same question identically. It is not re-exported from
 * `index.ts`: this is an internal detail of how drift is detected, not part of
 * the SDK's surface.
 */
export function canonicalJson(value) {
    if (Array.isArray(value))
        return `[${value.map(canonicalJson).join(',')}]`;
    if (isRecord(value)) {
        const body = Object.keys(value)
            .sort()
            .map((key) => `${JSON.stringify(key)}:${canonicalJson(value[key])}`)
            .join(',');
        return `{${body}}`;
    }
    // `JSON.stringify` returns `undefined` — not a string — for these three, and
    // the lib types do not say so. Naming them keeps the fallback honest instead
    // of hiding it behind a `??` the type checker reads as dead.
    if (value === undefined || typeof value === 'function' || typeof value === 'symbol') {
        return 'null';
    }
    return JSON.stringify(value);
}
function matchesDeclaredType(schema, value) {
    const type = schema['type'];
    if (type === undefined)
        return true;
    const types = Array.isArray(type) ? type : [type];
    return types.some((entry) => {
        switch (entry) {
            case 'string':
                return typeof value === 'string';
            case 'integer':
                return typeof value === 'number' && Number.isSafeInteger(value);
            case 'number':
                return typeof value === 'number';
            case 'boolean':
                return typeof value === 'boolean';
            case 'null':
                return value === null;
            case 'array':
                return Array.isArray(value);
            case 'object':
                return isRecord(value);
            default:
                return false;
        }
    });
}
/**
 * Refuse a schema that can never accept anything.
 *
 * Both shapes below compile, publish, and then reject every payload the platform
 * sends — so the action is dead for the life of the release and the only symptom
 * is `INVALID_PAYLOAD` acks nobody is watching for.
 */
function checkSatisfiable(key, schema) {
    const enumValues = schema['enum'];
    if (Array.isArray(enumValues)) {
        if (enumValues.length === 0)
            invalid(`live action '${key}' has an empty enum`);
        const usable = enumValues.filter((value) => matchesDeclaredType(schema, value));
        if (usable.length === 0) {
            invalid(`live action '${key}' declares an enum whose values do not match its own type, ` +
                'so no payload can ever satisfy it');
        }
    }
    if ('const' in schema && !matchesDeclaredType(schema, schema['const'])) {
        invalid(`live action '${key}' declares a const that its own type rejects`);
    }
    const properties = isRecord(schema['properties']) ? schema['properties'] : undefined;
    const required = schema['required'];
    if (Array.isArray(required) && schema['additionalProperties'] === false) {
        const missing = required.filter((name) => typeof name === 'string' && !(properties !== undefined && name in properties));
        if (missing.length > 0) {
            invalid(`live action '${key}' requires ${missing.join(', ')}, which it does not declare as a ` +
                'property — with additionalProperties: false no payload can satisfy both');
        }
    }
    for (const value of Object.values(properties ?? {})) {
        if (isRecord(value))
            checkSatisfiable(key, value);
    }
}
/**
 * Check one declaration hard enough that a bad one cannot reach a live stream.
 *
 * These duplicate rules the platform's manifest validator also enforces. That is
 * worth it because this runs at module load in the game's own dev server, and
 * the validator runs at publication — a build, a review and an upload apart.
 */
function checkDeclaration(key, body) {
    if (!KEY_PATTERN.test(key)) {
        invalid(`live action key '${key}' does not match ^[a-z][a-z0-9_]{1,63}$. ` +
            'Live action keys use underscores; it is gameKey that uses hyphens.');
    }
    if (key.startsWith(RESERVED_PREFIX) && !CANONICAL_RESERVED_KEYS.includes(key)) {
        invalid(`live action '${key}' is inside the platform-reserved ${RESERVED_PREFIX}* namespace. ` +
            `Only ${CANONICAL_RESERVED_KEYS.join(', ')} exist; publication refuses anything else.`);
    }
    if (!isRecord(body))
        invalid(`live action '${key}' must be an object`);
    // An unknown field is almost always a typo, and every one of them is silent:
    // `previewPayloads` is dropped, so the dev shell has nothing to send and the
    // preview is never validated against its own schema.
    const strayFields = Object.keys(body).filter((field) => !BODY_FIELDS.has(field));
    if (strayFields.length > 0) {
        invalid(`live action '${key}' has field(s) the manifest does not define: ${strayFields.join(', ')}. ` +
            `Expected one of ${[...BODY_FIELDS].join(', ')}.`);
    }
    const label = body['label'];
    if (typeof label !== 'string' || label.trim() === '' || label.length > 60) {
        invalid(`live action '${key}' needs a label of 1 to 60 characters`);
    }
    const description = body['description'];
    if (typeof description !== 'string' || description.trim() === '' || description.length > 400) {
        invalid(`live action '${key}' needs a description of 1 to 400 characters. It is the only channel ` +
            'from this game to the creator building the rule, so it is worth the space.');
    }
    if (typeof body['safetyClass'] !== 'string' || !SAFETY_CLASSES.has(body['safetyClass'])) {
        invalid(`live action '${key}' needs safetyClass COSMETIC, GAMEPLAY or INTERRUPTIVE, ` +
            `not ${JSON.stringify(body['safetyClass'])}`);
    }
    const schema = body['payloadSchema'];
    if (!isRecord(schema))
        invalid(`live action '${key}' needs a payloadSchema object`);
    if (schema['type'] !== 'object') {
        // The envelope's payload is always an object, so a root schema of any other
        // type rejects every delivery for the life of the release.
        invalid(`live action '${key}' declares a payloadSchema of type ${JSON.stringify(schema['type'])}. ` +
            'An action payload is always an object; the root schema has to say so.');
    }
    checkSatisfiable(key, schema);
    // Throws here, at import, rather than at the first action of a live session.
    const validate = compilePayloadSchema(schema);
    const limits = body['defaultLimits'];
    if (!isRecord(limits))
        invalid(`live action '${key}' needs defaultLimits`);
    const strayLimits = Object.keys(limits).filter((field) => !LIMIT_FIELDS.has(field));
    if (strayLimits.length > 0) {
        invalid(`live action '${key}' defaultLimits has unknown field(s): ${strayLimits.join(', ')}. ` +
            'The manifest schema is additionalProperties: false here, so publication would refuse it.');
    }
    const ranges = [
        ['ttlMs', 250, 300_000, true],
        ['maxPerMinute', 1, 10_000, true],
        ['queueDepth', 1, 1000, true],
        ['perActorCooldownMs', 0, 3_600_000, false],
        ['maxPayloadBytes', 64, 65_536, false],
    ];
    for (const [field, min, max, required] of ranges) {
        const value = limits[field];
        if (value === undefined) {
            if (required)
                invalid(`live action '${key}' needs defaultLimits.${field}`);
            continue;
        }
        if (typeof value !== 'number' || !Number.isInteger(value) || value < min || value > max) {
            invalid(`live action '${key}' needs defaultLimits.${field} as an integer between ` +
                `${String(min)} and ${String(max)}, not ${JSON.stringify(value)}`);
        }
    }
    if (typeof limits['overflowPolicy'] !== 'string' ||
        !OVERFLOW_POLICIES.has(limits['overflowPolicy'])) {
        invalid(`live action '${key}' needs a defaultLimits.overflowPolicy of ` +
            [...OVERFLOW_POLICIES].join(', '));
    }
    const preview = body['previewPayload'];
    if (preview !== undefined) {
        if (!isRecord(preview))
            invalid(`live action '${key}' previewPayload must be an object`);
        // A preview its own schema rejects is worse than none: it is what the dev
        // shell sends and what the Bridge's rule builder offers as a starting value,
        // so it would make every local test exercise a rejection path.
        if (!validate(preview)) {
            invalid(`live action '${key}' has a previewPayload its own payloadSchema rejects. ` +
                'The dev shell and the rule builder both send it, so it has to be a valid payload.');
        }
    }
    return {
        ...body,
        key,
    };
}
export function defineActions(declarations) {
    const pairs = Array.isArray(declarations)
        ? declarations.map((entry) => {
            if (!isRecord(entry) || typeof entry['key'] !== 'string') {
                invalid('every entry of a liveActions array needs a string key');
            }
            const { key, ...body } = entry;
            return [key, body];
        })
        : Object.entries(declarations);
    /*
     * Kept twice on purpose: the array is the declaration order every caller sees,
     * and the map is the lookup. Deriving one from the other at each use would
     * mean a `Map.get` whose result the type system cannot know is present.
     */
    const ordered = [];
    const entries = new Map();
    for (const [key, body] of pairs) {
        if (entries.has(key))
            invalid(`live action '${key}' is declared more than once`);
        const entry = checkDeclaration(key, body);
        entries.set(key, entry);
        ordered.push(entry);
    }
    const keys = ordered.map((entry) => entry.key);
    /*
     * A deep copy, structural rather than field-by-field.
     *
     * `checkDeclaration` already refuses a field the manifest does not define, so
     * there is nothing unknown left to preserve — but copying by structure keeps
     * the two decisions independent. A clone assembled from a hard-coded field
     * list would quietly delete anything that check later starts allowing, and
     * `manifestFragment()` writes its result straight into `manifest.json`.
     */
    const clone = (entry) => structuredClone(entry);
    return {
        keys,
        has(key) {
            return entries.has(key);
        },
        get(key) {
            const entry = entries.get(key);
            if (entry === undefined) {
                invalid(`no live action '${key}' is declared. Declared: ${keys.join(', ')}`);
            }
            return clone(entry);
        },
        manifestFragment() {
            return ordered.map(clone);
        },
        register(target, handlers, options = {}) {
            if (!isRecord(handlers))
                invalid('register() needs an object of handlers keyed by action key');
            const exempt = new Set(options.allowUnhandled ?? []);
            const alreadyRegistered = new Set(target.registeredActionKeys ?? []);
            const provided = Object.keys(handlers);
            const notFunctions = provided.filter((key) => typeof handlers[key] !== 'function');
            if (notFunctions.length > 0) {
                // Reported before anything else, because both other checks would
                // misdiagnose it: a called-instead-of-passed handler looks like a
                // missing one, and a typo'd key with a non-function value looks like
                // nothing at all.
                invalid(`handler(s) that are not functions: ${notFunctions.join(', ')}. ` +
                    'A common cause is calling the handler instead of passing it — ' +
                    'spawn_wave: spawnWave() rather than spawn_wave: spawnWave.');
            }
            const undeclared = provided.filter((key) => !entries.has(key));
            if (undeclared.length > 0) {
                invalid(`handler registered for undeclared live action(s): ${undeclared.join(', ')}. ` +
                    'The platform only ever delivers a declared key, so these handlers can never fire. ' +
                    `Declared: ${keys.join(', ')}.`);
            }
            /*
             * A reserved key never needs a handler here.
             *
             * `enableStreamMode()` registers the three `tp_live_*` actions itself, and
             * requiring one would make registration order load-bearing: calling
             * `register` first would throw, calling it second would not. Making them
             * unconditionally optional removes the ordering constraint entirely.
             */
            const missing = keys.filter((key) => !provided.includes(key) &&
                !key.startsWith(RESERVED_PREFIX) &&
                !alreadyRegistered.has(key) &&
                !exempt.has(key));
            if (missing.length > 0) {
                invalid(`declared live action(s) with no handler: ${missing.join(', ')}. ` +
                    'The platform will deliver them and the game will answer UNKNOWN_ACTION on stream. ' +
                    'Add a handler, remove the declaration, or list the key in allowUnhandled.');
            }
            const unregisters = [];
            try {
                for (const entry of ordered) {
                    const handler = handlers[entry.key];
                    if (typeof handler !== 'function')
                        continue;
                    if (alreadyRegistered.has(entry.key)) {
                        invalid(`live action '${entry.key}' already has a handler on this game. ` +
                            (entry.key.startsWith(RESERVED_PREFIX)
                                ? 'enableStreamMode() registered it; drop it from this map or pass ' +
                                    'render:false and handle it yourself, not both.'
                                : 'Call the function returned by the previous register() before registering again.'));
                    }
                    unregisters.push(target.onAction(entry.key, {
                        validate: entry.payloadSchema,
                        handle: (payload, context) => handler(payload, context),
                    }));
                }
            }
            catch (error) {
                // All or nothing. A half-registered set is the state in which some
                // actions work on stream and others answer UNKNOWN_ACTION, which reads
                // as an intermittent platform fault rather than as a bug here.
                for (const off of unregisters)
                    off();
                unregisters.length = 0;
                throw error;
            }
            return () => {
                for (const off of unregisters)
                    off();
                unregisters.length = 0;
            };
        },
        assertMatchesManifest(manifestLiveActions) {
            if (!Array.isArray(manifestLiveActions)) {
                invalid('assertMatchesManifest() needs the manifest liveActions array');
            }
            const fromManifest = new Map();
            for (const entry of manifestLiveActions) {
                if (!isRecord(entry) || typeof entry['key'] !== 'string') {
                    invalid('every entry of manifest.liveActions needs a string key');
                }
                fromManifest.set(entry['key'], entry);
            }
            /*
             * Absence is checked before content, because it is the more specific
             * answer. A manifest missing the action entirely and a manifest whose copy
             * of it has drifted are different mistakes with different fixes.
             */
            const absent = keys.filter((key) => !fromManifest.has(key));
            if (absent.length > 0) {
                invalid(`manifest.json does not declare: ${absent.join(', ')}. ` +
                    'The platform reads manifest.json and nothing else, so these actions do not exist ' +
                    'in production. Write manifestFragment() into it.');
            }
            // The other direction, which the platform will happily deliver: an entry
            // in the manifest that nothing here declares reaches a game with no
            // handler. Reserved keys are excluded — the platform owns those.
            const extra = [...fromManifest.keys()].filter((key) => !entries.has(key) && !key.startsWith(RESERVED_PREFIX));
            if (extra.length > 0) {
                invalid(`manifest.json declares action(s) these declarations do not: ${extra.join(', ')}. ` +
                    'The platform will deliver them and the game will answer UNKNOWN_ACTION.');
            }
            const drifted = ordered.filter((entry) => canonicalJson(entry) !== canonicalJson(fromManifest.get(entry.key)));
            if (drifted.length > 0) {
                invalid(`manifest.json disagrees with these declarations for: ` +
                    `${drifted.map((entry) => entry.key).join(', ')}. ` +
                    'Write manifestFragment() into manifest.json before publishing.');
            }
        },
    };
}
